'use client'

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { useRouter } from 'next/navigation'
import { useStytchMember } from '@stytch/nextjs/b2b'
import { clearStytchCookies } from '@/utilities/stytch/stytchClient'
import { resetAnalytics } from '@/utilities/analytics/userAnalytics'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { clearAllFlowData } from '@/flows/engine/clearFlowData'
import { clearAuthUserId } from '@/platform/http'
import { removeSelectedClientId } from '@/utilities/client/selectedClientIdStorage'
import { clearAllAnalyticsQuestionHistory } from '@/utilities/client/analyticsQuestionHistoryStorage'
import { clearAllAnalyticsProgramSelection } from '@/utilities/client/analyticsProgramSelectionStorage'
import { resetAllInMemoryFilterCommits } from '@/components/client/orders/filters/inMemoryFilterCommit'
import {
  endImpersonationHandoff,
  getImpersonationHandoffExpectedMemberId,
  isImpersonationHandoffInProgress,
  subscribeImpersonationHandoff,
} from '@/utilities/stytch/impersonationHandoff'

interface UseSessionMonitorOptions {
  redirectTo?: string
  enabled?: boolean
}

export const useSessionMonitor = (options: UseSessionMonitorOptions = {}) => {
  const { redirectTo = '/login', enabled = true } = options
  const router = useRouter()
  const { member, isInitialized } = useStytchMember()
  const wasAuthenticatedRef = useRef<boolean>(false)
  const impersonationHandoffInProgress = useSyncExternalStore(
    subscribeImpersonationHandoff,
    isImpersonationHandoffInProgress,
    isImpersonationHandoffInProgress,
  )
  const impersonationHandoffExpectedMemberId = useSyncExternalStore(
    subscribeImpersonationHandoff,
    getImpersonationHandoffExpectedMemberId,
    getImpersonationHandoffExpectedMemberId,
  )

  const onSessionExpiration = useCallback(() => {
    // Track session expiration event
    trackEvent('session:expire')

    // Reset analytics state
    resetAnalytics()

    // Clear any remaining Stytch cookies using the existing utility
    // This is likely not needed as the Stytch client SDK handles this automatically, but keeping it as a safety net
    clearStytchCookies()

    // Clear cached x-user-identity (local portal-api only) to avoid stale identity reuse
    clearAuthUserId()

    // Clear selected client so pre-auth pages do not reuse the previous session's partner
    removeSelectedClientId()

    // Program selection is member-scoped, but leftover keys must not outlive the session
    clearAllAnalyticsProgramSelection()

    // Clear PHI-bearing order filters so they cannot survive session expiration
    resetAllInMemoryFilterCommits()

    // Clear all flow-related data from localStorage for security/privacy
    clearAllFlowData()

    // Ask Your Data history is free-text and must not outlive the session
    clearAllAnalyticsQuestionHistory()

    // Get current path for redirect
    const currentPath = window.location.pathname

    // Create login URL with redirect
    const loginUrl = new URL(redirectTo, window.location.origin)
    if (currentPath !== redirectTo) {
      loginUrl.searchParams.set('redirectTo', currentPath)
    }

    loginUrl.searchParams.set('reason', 'session-expired')

    // Redirect to login
    router.push(loginUrl.toString())
  }, [redirectTo, router])

  useEffect(() => {
    // Early return if not enabled or not initialized. Also ignore member flicker
    // during Users-table impersonation handoff so we do not treat the swap as expiry.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
    if (!enabled || !isInitialized) return

    if (impersonationHandoffInProgress) {
      if (
        impersonationHandoffExpectedMemberId &&
        member?.member_id === impersonationHandoffExpectedMemberId
      ) {
        wasAuthenticatedRef.current = true
        endImpersonationHandoff()
      }
      return
    }

    // Track authentication state changes
    if (member && !wasAuthenticatedRef.current) {
      wasAuthenticatedRef.current = true
    } else if (!member && wasAuthenticatedRef.current) {
      // User was authenticated but now is not - session expired
      wasAuthenticatedRef.current = false
      onSessionExpiration()
    }
  }, [
    member,
    isInitialized,
    enabled,
    onSessionExpiration,
    impersonationHandoffInProgress,
    impersonationHandoffExpectedMemberId,
  ])
}
